import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { PageHero } from '@/components/layout/PageHero';
import { StatTile } from '@/components/StatTile';
import { useMe } from '@/features/auth/hooks';
import { useCompanies } from '@/features/customer/hooks';
import { ActivityFeed } from '@/features/dashboard/components/ActivityFeed';
import { BarList } from '@/features/dashboard/components/BarList';
import { DonutChart } from '@/features/dashboard/components/DonutChart';
import { NeedsAttentionList } from '@/features/dashboard/components/NeedsAttentionList';
import { RunningNowList } from '@/features/dashboard/components/RunningNowList';
import { useDashboard, useDashboardSectionLoadMore, useOnboardingState } from '@/features/dashboard/hooks';
import {
  donutSegments,
  mapNeedsAttention,
  mapRecentActivity,
  mapRunningNow,
  marketCoverageBars,
  targetMarketCoverage,
} from '@/features/dashboard/insights';
import { GettingStartedCard } from '@/features/dashboard/components/GettingStartedCard';
import { gettingStartedSteps } from '@/features/dashboard/onboarding';
import { useTeamMembers } from '@/features/team/hooks';
import { useTourTrigger } from '@/features/tour/useTour';

/**
 * Dashboard — portfolio overview, rendered from the SERVER-SIDE rollup
 * (`GET /api/dashboard`). The dashboard no longer fetches every product
 * summary — all counting happens in SQL; the client only labels, styles,
 * and merges the declared target markets (from the Company Account) into
 * the coverage view.
 *
 *   - KPI row: Active Products / Runs In Flight / Needs Attention /
 *     Products with Dossier / Target Markets (vs declared)
 *   - Insight cards: portfolio mix donut, per-market coverage bars
 *   - Work row: failed runs needing a retry, live in-flight runs, and
 *     recent completions — all deep-linking to result pages.
 */
export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
});

function DashboardPage() {
  const dashboardQuery = useDashboard();
  // Wait for the rollup to load before freezing the tour's present-step set —
  // otherwise a first-visit user who already has products misses the two
  // `data && hasProducts`-gated steps below (an empty dashboard is "ready" too).
  useTourTrigger('dashboard', dashboardQuery.data !== undefined);
  const companiesQuery = useCompanies();
  const me = useMe();
  const welcomeName = me.data?.name?.trim() || '—';

  const data = dashboardQuery.data;
  const hasProducts = (data?.total_products ?? 0) > 0;
  // Each list's first 5 ride in the single rollup above (one request). "Load
  // more" fetches the next pages on demand — no extra request until a user
  // actually pages.
  const attention = useDashboardSectionLoadMore(
    'needs-attention', data?.needs_attention, data?.needs_attention_cursor, { enabled: hasProducts },
  );
  const running = useDashboardSectionLoadMore(
    'running-now', data?.running_now, data?.running_now_cursor, { enabled: hasProducts },
  );
  const activity = useDashboardSectionLoadMore(
    'recent-activity', data?.recent_activity, data?.recent_activity_cursor, { enabled: hasProducts },
  );

  // Declared target markets — the union of jurisdiction codes across the
  // account's companies (Company Account wizard). Empty for accounts that
  // haven't declared markets; coverage falls back to activity-based.
  const declaredMarkets = [
    ...new Set(
      (companiesQuery.data ?? []).flatMap((c) =>
        c.jurisdictions.map((j) => j.market_code),
      ),
    ),
  ].sort();
  const intentCoverage = data
    ? targetMarketCoverage(data.markets_covered, declaredMarkets)
    : null;

  // First-run onboarding gate — the SAME hook the sidebar + mobile drawer
  // use, so the checklist and the lock states can never disagree.
  const onboarding = useOnboardingState();
  // "Ran first agent" = ANY run activity (in flight, completed, or even
  // failed — they ran one). Only meaningful once the rollup has loaded.
  const ranFirstAgent =
    !!data &&
    (data.in_flight_runs > 0 ||
      data.recent_activity_count > 0 ||
      data.needs_attention_count > 0);
  // Team roster (admin-gated endpoint) — only fetched for users who can
  // actually see the checklist. >1 row = a teammate or pending invite.
  const teamQuery = useTeamMembers(onboarding.canEditCustomer);
  const teamInvited = (teamQuery.data?.length ?? 1) > 1;
  const checklist = data
    ? gettingStartedSteps(onboarding, ranFirstAgent, teamInvited)
    : null;

  return (
    <>
      <PageHero
        title={<>Welcome Back, {welcomeName}</>}
        subtitle="Manage your regulatory compliance, clinical protocols, and product submissions from one unified platform."
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20">
        {/* KPI grid — 2-up mobile, 3-up sm, 5-up from lg */}
        <div
          data-tour="dashboard-metrics"
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 mt-7 mb-8"
        >
          <StatTile label="Active Products" value={data?.total_products ?? '—'} />
          <StatTile label="Runs In Flight" value={data?.in_flight_runs ?? '—'} />
          <StatTile label="Needs Attention" value={data?.needs_attention_count ?? '—'} />
          <StatTile label="Products with Dossier" value={data?.products_with_dossier ?? '—'} />
          {intentCoverage ? (
            <StatTile
              label="Target Markets"
              value={`${intentCoverage.covered}/${intentCoverage.declared}`}
            />
          ) : (
            <StatTile label="Markets Covered" value={data?.markets_covered.length ?? '—'} />
          )}
        </div>

        {checklist && <GettingStartedCard steps={checklist} />}

        {data && hasProducts && (
          <>
            {/* Insight cards */}
            <div
              data-tour="dashboard-portfolio-coverage"
              className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8"
            >
              <InsightCard title="Portfolio Mix">
                <DonutChart segments={donutSegments(data.by_type)} />
              </InsightCard>
              <InsightCard title="Market Coverage">
                <p className="mb-3 text-[11.5px] text-muted">
                  Products with completed agent work, per{' '}
                  {declaredMarkets.length > 0 ? 'target market' : 'market'}.
                </p>
                <BarList items={marketCoverageBars(data.market_coverage, declaredMarkets)} />
              </InsightCard>
            </div>

            {/* Work row — failures needing a retry, live in-flight runs,
                and completions. Observational only: the platform doesn't
                prescribe a pipeline order. */}
            <div
              data-tour="dashboard-activity"
              className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start"
            >
              <InsightCard title="Needs Attention">
                <NeedsAttentionList
                  items={mapNeedsAttention(attention.items)}
                  total={data.needs_attention_count}
                  hasMore={attention.hasMore}
                  isFetchingMore={attention.isFetchingMore}
                  onLoadMore={attention.loadMore}
                />
              </InsightCard>
              <InsightCard title="Running Now">
                <RunningNowList
                  runs={mapRunningNow(running.items)}
                  total={data.in_flight_runs}
                  hasMore={running.hasMore}
                  isFetchingMore={running.isFetchingMore}
                  onLoadMore={running.loadMore}
                />
              </InsightCard>
              <InsightCard title="Recent Activity">
                <ActivityFeed
                  events={mapRecentActivity(activity.items)}
                  total={data.recent_activity_count}
                  hasMore={activity.hasMore}
                  isFetchingMore={activity.isFetchingMore}
                  onLoadMore={activity.loadMore}
                />
              </InsightCard>
            </div>
          </>
        )}

        {data && !hasProducts && (
          <div className="rounded-xl border border-line bg-card shadow-card px-6 py-10 text-center">
            <div className="text-[15px] font-semibold text-navy">
              Your dashboard lights up with your first product
            </div>
            <p className="mt-1.5 text-[13px] text-muted">
              Portfolio charts, market coverage, and run activity all derive from
              your submitted products. Use the sidebar to get started.
            </p>
          </div>
        )}
      </main>
    </>
  );
}

/** Shared white card chrome for the insight panels. */
function InsightCard({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-xl border border-line bg-card shadow-card overflow-hidden ${className ?? ''}`}
    >
      <h2 className="px-5 pt-4 pb-3 text-[11px] tracking-[0.16em] uppercase text-muted font-bold">
        {title}
      </h2>
      <div className="px-5 pb-5">{children}</div>
    </section>
  );
}
